import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import useApi from "../../core/customhook/useApi";
import Loader from "../../core/common/selfLoder";
import { all_routes } from "../router/all_routes";


/* -------------------- Types -------------------- */

interface ProrationItem {
    monthly_gross: string;
    basic: string;
    hra: string;
    children_education: string;
    personal_allowance: string;
    esic: string;
    accidental_insurance: string;
    medical_insurance: string;
    employee_pf_emp: string;
    employee_pf_com: string;
    variable_pay: string;
}

type Remark = {
    id: number;
    annexure_id: number;
    type: string; // Type corresponds to keys in the `remark` state (e.g., grossRemark, hraRemark)
    remark: string; // The actual remark text
};

interface ApiRemark {
    gross?: string;
    hra?: string;
    basic?: string;
    children_education?: string;
    personal_allowance?: string;
    esic?: string;
    accidental_insurance?: string;
    medical_insurance?: string;
    employee_pf_emp?: string;
    employee_pf_com?: string;
    variable_pay?: string;
}

type RemarkState = {
    grossRemark: string;
    hraRemark: string;
    basicRemark: string;
    childrenAlRemark: string;
    personalAlRemark: string;
    esicRemark: string;
    accidentalInsRemark: string;
    medicalInsRemark: string;
    employeepfRemark: string;
    employerpfRemark: string;
    variableRemark: string;
};

const AnnexurePage = () => {
    const { data: response, loading: isAnnexureLoading, error } = useApi("/api/employee/annexure", {
        method: "GET",
        autoCall: true,
    });

    const location = useLocation();
    const [proration, setProration] = useState<ProrationItem | null>(null);
    const [selectedYear, setSelectedYear] = useState<string>("");
    const [remarks, setRemarks] = useState<RemarkState>({
        grossRemark: "",
        hraRemark: "",
        basicRemark: "",
        childrenAlRemark: "",
        personalAlRemark: "",
        esicRemark: "",
        accidentalInsRemark: "",
        medicalInsRemark: "",
        employeepfRemark: "",
        employerpfRemark: "",
        variableRemark: "",
    });
    const { data: years, loading: isYearloading, error: yearError } = useApi("/api/employee/getyears", {
        method: "GET",
        autoCall: true,
    })

    useEffect(() => {
        if (!response?.data || !selectedYear) return;

        const filteredRecord = response.data.find(
            (item: any) => item.year === selectedYear
        );

        if (!filteredRecord) return;

        setProration(filteredRecord);

        const updatedRemarks: RemarkState = {
            grossRemark: "",
            hraRemark: "",
            basicRemark: "",
            childrenAlRemark: "",
            personalAlRemark: "",
            esicRemark: "",
            accidentalInsRemark: "",
            medicalInsRemark: "",
            employeepfRemark: "",
            employerpfRemark: "",
            variableRemark: "",
        };

        if (Array.isArray(filteredRecord.remarks)) {
            filteredRecord.remarks.forEach(
                ({ type, remark }: { type: keyof RemarkState; remark: string }) => {
                    if (type in updatedRemarks) {
                        updatedRemarks[type] = remark;
                    }
                }
            );
        }

        setRemarks(updatedRemarks);
    }, [response, selectedYear]);

    const isCompensationActive = location.pathname === all_routes.compensationPage;
    const isAnnexureActive = location.pathname === all_routes.annexurePage;


    const getButtonStyle = (isActive: boolean) => ({
        backgroundColor: isActive ? "#2e3294" : "transparent",
        color: isActive ? "#ffffff" : "#2e3294",
        border: "1px solid #2e3294"
    });

    return (
        <>
            <Loader loading={isAnnexureLoading} />
            <div className="page-wrapper">
                <div className="content">
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Annexure A</h2>
                            <nav>
                                <ol className="breadcrumb mb-0 gap-2">
                                    <li className="breadcrumb-item">
                                        <Link to="/dashboard">
                                            <i className="ti ti-smart-home" /> /
                                        </Link>
                                    </li>
                                    <Link to={"/compensation"}>
                                        <li className="breadcrumb-item"> Compensation / </li>
                                    </Link>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Annexure
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <div className=" d-flex align-items-center justify-content-start flex-wrap row-gap-3">
                                <Link to={all_routes.compensationPage}>
                                    <button
                                        className="btn px-2 mx-2 px-sm-4"
                                        style={getButtonStyle(isCompensationActive)}
                                    >
                                        Compensation
                                    </button>
                                </Link>
                                <Link to={all_routes.annexurePage}>
                                    <button
                                        className="btn px-2 px-sm-4"
                                        style={getButtonStyle(isAnnexureActive)}
                                    >
                                        Annexure
                                    </button>
                                </Link>
                            </div>
                            <div className="d-flex flex-row gap-2">
                                <select
                                    className="form-control w-auto"
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                >
                                    <option value="">Select Year</option>
                                    {years?.data?.map((val: any) => (
                                        <option value={val.year} key={val.ID}>
                                            {val.year}
                                        </option>
                                    ))}
                                </select>

                            </div>
                        </div>
                        <div className="card-body">
                            {proration ? (
                                <div >
                                    <SalaryTable proration={proration} remark={remarks} />
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    Please select a financial year
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                <div className="footer d-flex align-items-center justify-content-center border-top bg-white p-3">
                    <p className="mb-0"> © {new Date().getFullYear()}  WeConnect. All rights reserved.</p>
                </div>
            </div>
        </>
    );
};
const SalaryTable = ({
    proration,
    remark,
}: {
    proration: ProrationItem;
    remark: RemarkState;
}) => {

    console.log("Remark :", remark)
    const num = (v: any) => Number(v) || 0;

    const basic = num(proration.basic);
    const hra = num(proration.hra);
    const children = num(proration.children_education);
    const personal = num(proration.personal_allowance);
    const emp_pf_emp = num(proration.employee_pf_emp);
    const emp_pf_com = num(proration.employee_pf_com);
    const medical = num(proration.medical_insurance);
    const accidental = num(proration.accidental_insurance);
    const esic = num(proration.esic);
    const variable = num(proration.variable_pay);
    const monthly_gross = num(proration.monthly_gross);

    const netTakeMonthly =
        monthly_gross - emp_pf_emp - accidental - medical - esic;

    const totalCTC = monthly_gross * 12 + (emp_pf_com ?? 0 * 12) + variable
    const rows = [
        { label: "Basic", value: basic, remarks: remark.basicRemark },
        { label: "HRA", value: hra, remarks: remark.hraRemark },
        {
            label: "Children Education Allowance",
            value: children,
            remarks: remark.childrenAlRemark,
        },
        {
            label: "Personal Allowance",
            value: personal,
            remarks: remark.personalAlRemark,
        },
        { label: "Monthly Gross", value: monthly_gross, remarks: remark.grossRemark },

        { label: "Employee PF", value: emp_pf_emp, remarks: remark.employeepfRemark },
        {
            label: "Accidental Insurance",
            value: accidental,
            remarks: remark.accidentalInsRemark,
        },
        {
            label: "Medical Insurance",
            value: medical,
            remarks: remark.medicalInsRemark,
        },
        { label: "ESIC", value: esic, remarks: remark.esicRemark },

        { label: "Net Take Home", value: netTakeMonthly, annual: netTakeMonthly * 12 },

        { label: "Employer PF", value: emp_pf_com, remarks: remark.employerpfRemark },

        {
            label: "Variable Pay",
            value: 0,
            annual: variable,
            remarks: remark.variableRemark,

        },
        {
            label: "Total CTC",
            value: 0,
            annual: totalCTC,
            remarks: "",

        },
    ];

    return (
        <div className="d-flex justify-content-center align-items-center text-black">
            <table className="table-bordered my-5" style={{ width: "80%" }}>
                <thead>
                    <tr className="table-active">
                        <th className="text-center">Component</th>
                        <th className="text-center">Per Month</th>
                        <th className="text-center">Per Annual</th>
                        <th className="text-center">Remarks</th>
                    </tr>
                </thead>

                <tbody>
                    {rows.map((row, index) => {
                        const perMonth = row.value.toFixed(0);
                        const perAnnual = ((row.annual ?? row.value * 12) as number).toFixed(
                            0,
                        );

                        const highlight =
                            row.label === "Monthly Gross" ||
                            row.label === "Net Take Home" ||
                            row.label === "Total CTC";

                        return (
                            <tr
                                key={index}
                                style={{
                                    backgroundColor: highlight ? "#c7c7c7" : "transparent",
                                }}
                            >
                                <td>{row.label}</td>
                                <td className="text-center">{Number(perMonth) || ""}</td>
                                <td className="text-center">{Number(perAnnual) || ""}</td>
                                <td className="text-center">{row.remarks || ""}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default AnnexurePage;
